import { Theme } from '@mui/material'

import makeStyles from '@mui/styles/makeStyles'

const bp = 'sm'

type StylesType = {
  canEdit?: boolean
  isCreatingTask?: boolean
  smallMedia?: Boolean
}

const useStyles = makeStyles<Theme, StylesType>(theme => ({
  parentTitle: {
    fontSize: theme.size.base,
    color: theme.palette.primary.main,
    cursor: 'pointer'
  },

  nameSection: {
    position: 'static',
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'space-between',
    borderBottom: '2px solid #F1F1F1'
  },

  textField: {
    flex: '1',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    marginBottom: '10px',
    boxSizing: 'border-box',

    '&:focus-within': {
      borderBottom: '1px solid grey'
    },

    '& .text': {
      opacity: '0.7',
      fontSize: theme.size.lg,
      background: 'transparent'
    },

    '& .adorn': {
      marginRight: '10px'
    }
  },

  padded: {
    padding: '1.5rem 0 1rem 0',
    marginBottom: '0'
  },

  input: {
    flex: '1',
    outline: 'none',
    border: 'none',
    maxWidth: ({ isCreatingTask }) => (isCreatingTask ? '100%' : '68%'),
    overflow: 'hidden',
    textOverflow: 'ellipsis',

    [theme.breakpoints.down(bp)]: {
      maxWidth: '100%'
    }
  },

  bodyWrapper: {
    display: 'flex',

    '& .editor-input': {
      minHeight: 'unset'
    },

    [theme.breakpoints.down(bp)]: {
      marginTop: '-2rem'
    }
  },

  body: {
    marginTop: '10px',
    width: '100%',

    [theme.breakpoints.up(bp)]: {
      height: '100%'
    },

    '& > *': {
      outline: 'none',
      border: 'none',

      [theme.breakpoints.up(bp)]: {
        height: '100%'
      }
    },

    '& .text': {
      overflow: 'auto !important',
      width: '100%',
      backgroundColor: 'transparent',
      color: '#323436',
      resize: 'none',

      [theme.breakpoints.down('md')]: {
        marginBottom: '1.5rem'
      },

      '& div > div > textarea.text__input': {
        padding: '0.25rem 1.25rem 0.25rem 0rem !important',
        overflow: 'hidden !important',
        fontSize: `${theme.size.sm} !important`
      },

      '& div > div > .text__highlighter': {
        height: 'auto !important'
      },

      '& div > .text__control ': {
        height: 'auto !important'
      }
    }
  },

  description: {
    marginBottom: '1rem',
    height: '1rem',
    cursor: 'default',
    display: 'flex',
    alignItems: 'center',
    gap: 4,

    [theme.breakpoints.down(bp)]: {
      fontSize: theme.size.sm,
      marginTop: '2.5rem',
      marginBottom: '0.5rem'
    }
  },

  editor: {
    '& .editor': {
      border: '1px solid #E0E0E0',
      borderRadius: '4px'
    },

    '& .editor-input': {
      minHeight: '50px !important'
    }
  },

  subtask: {
    height: '20px',
    paddingBottom: '1rem',
    display: 'flex',
    alignItems: 'center',
    cursor: ({ canEdit }) => (canEdit ? 'pointer' : 'default')
  },

  transparent: {
    '& > div > input': {
      border: 'none',
      backgroundColor: 'white',
      outline: 'none',
      padding: '0',
      width: '50%'
    },

    '& > div > input:focus': {
      boxShadow: 'none',
      borderColor: 'none'
    }
  },

  // Everything under this is used in sub components
  selections: {
    display: 'grid',
    gridTemplateColumns: 'calc(100% / 3) calc(100% / 3) calc(100% / 3)',
    gridColumnGap: '10px',
    marginBottom: '0.5rem',
    width: 'calc(100% - 20px)'
  },

  picker: {
    width: '100%',
    height: '100%',
    cursor: 'pointer'
  },

  pickerInput: {
    position: 'relative',
    width: '100%',

    '& .adorn': {
      position: 'absolute',
      top: '50%',
      right: '5px',
      transform: 'translate(-5px, -50%)',
      fontSize: theme.size.lg,
      color: 'white'
    }
  },

  select: {
    paddingRight: '10px'
  },

  switch: {
    zIndex: 2,
    position: 'absolute',
    right: '0',
    top: '0',
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    color: '#32A6DC',
    cursor: 'pointer',
    userSelect: 'none',

    '&:hover': {
      textDecoration: 'underline'
    }
  },

  taskInfo: {
    display: 'flex'
  },

  label: {
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      marginRight: '0.5rem'
    }
  },

  subtaskInput: {
    width: '70%'
  },

  buttonIcon: {
    marginRight: '20px',
    fontSize: theme.size.lg
  },

  mobile: {
    justifyContent: 'flex-start'
  },

  addsubtasksRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 6,
    marginRight: 6,
    gap: 6,
    alignItems: 'center'
  },

  cannedButton: {
    display: 'flex',
    placeItems: 'center',
    borderRadius: '10px',
    padding: '6px',
    gap: 4,
    minWidth: 'auto',
    width: 'auto',
    height: 'auto'
  },

  tabs: {
    position: 'relative',
    top: '0',
    left: '50%',
    transform: 'translateX(-50%)',
    marginTop: '-49px',
    marginBottom: '10px',
    maxWidth: 'fit-content',

    [theme.breakpoints.down('md')]: {
      marginTop: '0.5rem'
    }
  },

  tab: {
    fontSize: theme.size.sm
  },

  descriptionContainer: {
    [theme.breakpoints.down(451)]: {
      border: '1px solid #C6C6C6',
      marginTop: '1rem',
      borderRadius: 2,
      '& .toolbar': {
        display: 'none'
      }
    }
  },

  icon: {
    transform: 'rotate(90deg)'
  },

  extraBtn: {
    width: 'auto',
    padding: '0 1rem',
    fontSize: theme.size.sm
  }
}))

export default useStyles
